import React from 'react'

import clsx from 'clsx'

import ClickoutWrapper from 'components/ClickoutWrapper'
import CtaButton from 'components/CtaButton'
import { FixedImage } from 'components/NextImage'
import Paper from 'components/Paper'
import TruncatedText from 'components/TruncatedText'
import { useEventContext } from 'context/EventContext'
import openUrl from 'helpers/openUrl'
import { getengagementDestination } from 'helpers/reviews'
import { revealSEOUrl } from 'helpers/SEOHelper'

import ListingInfo from './components/ListingInfo'

import classes from './AlternativeCard.module.scss'

/** @type {import("./types").default} */
const AlternativeCard = ({
  className,
  compareUrl,
  ctaUrl,
  detailUrl,
  isCompareUrlSeo = false,
  isPremium = false,
  logoUrl,
  name,
  rating,
  reviewsNum,
  reviewsUrl,
  shortDescription,
  tagline,
  listingPosition = 0,
  productId,
}) => {
  const { addEvent, sendEvent } = useEventContext()

  return (
    <Paper className={clsx('AlternativeCard', classes.root, className)} elevation={2}>
      <div className={classes.header}>
        <ClickoutWrapper
          url={ctaUrl}
          isClickout={isPremium}
          buyerIntentProductId={productId}
          buyerIntentSourceLocation="compare/mobile-alternative:logo"
          buyerIntentTrackAdClick={isPremium}
          data-testid="AlternativeCard-logo-link"
          listingPosition={listingPosition}
          {...addEvent({
            productClickPosition: listingPosition,
            engagementComponent: 'product_alternatives_card-item',
            engagementDestination: isPremium ? 'go-to_vendor-website' : 'go-to_product_details',
            engagementDetails: 'logo_product',
            name: isPremium ? 'engagement_product_clickout' : 'engagement_product_click',
          })}
        >
          <div className={classes.logoContainer}>
            <FixedImage className={classes.logo} fetchWidth={72} width={72} src={logoUrl} alt={`${name} logo`} />
          </div>
        </ClickoutWrapper>
        <ListingInfo
          listingPosition={listingPosition}
          ctaUrl={ctaUrl}
          isPremium={isPremium}
          name={name}
          productId={productId}
          onCtaClick={() =>
            sendEvent({
              productClickPosition: listingPosition,
              engagementComponent: 'product_alternatives_card-item',
              engagementDestination: isPremium ? 'go-to_vendor-website' : 'go-to_product_details',
              engagementDetails: 'text-link_product-name',
              name: isPremium ? 'engagement_product_clickout' : 'engagement_product_click',
            })
          }
          onReviewsNumberClick={() =>
            sendEvent({
              productClickPosition: listingPosition,
              engagementComponent: 'product_alternatives_card-item',
              engagementDestination: getengagementDestination(reviewsUrl),
              engagementDetails: 'text-link_number-reviews',
              name: 'engagement_product_click',
            })
          }
          rating={rating}
          reviewsNum={reviewsNum}
          reviewsUrl={reviewsUrl}
          tagline={tagline}
        />
      </div>
      <TruncatedText
        className={classes.truncatedText}
        href={detailUrl}
        label={`Read more about ${name}`}
        text={shortDescription}
        onClick={() =>
          sendEvent({
            engagementComponent: 'product_alternatives_card-item',
            engagementDestination: 'go-to_product_details',
            engagementDetails: 'text-link_read-more-about-product',
            name: 'engagement_product_click',
          })
        }
      />
      <div>
        <ClickoutWrapper
          url={ctaUrl}
          isClickout={isPremium}
          buyerIntentProductId={productId}
          buyerIntentSourceLocation="compare/mobile-alternative:main-cta"
          buyerIntentTrackAdClick={isPremium}
          listingPosition={listingPosition}
          {...addEvent({
            productClickPosition: listingPosition,
            engagementComponent: 'product_alternatives_card-item',
            engagementDestination: isPremium ? 'go-to_vendor-website' : 'go-to_product_details',
            engagementDetails: isPremium ? 'button_visit-website' : 'button_learn-more',
            name: isPremium ? 'engagement_product_clickout' : 'engagement_product_click',
          })}
        >
          <CtaButton className={classes.primaryCta} label={isPremium ? 'visit website' : 'learn more'} variant="text" />
        </ClickoutWrapper>
        {compareUrl && (
          <CtaButton
            href={isCompareUrlSeo ? revealSEOUrl(compareUrl) : undefined}
            label="compare"
            onClick={() => {
              if (!isCompareUrlSeo) openUrl(revealSEOUrl(compareUrl))
            }}
            secondary
            variant="text"
            {...addEvent({
              engagementComponent: 'product_alternatives_card-item',
              engagementDestination: 'go-to_product_comparisons',
              engagementDetails: 'button_compare',
              name: 'engagement_cta_compare',
            })}
          />
        )}
      </div>
    </Paper>
  )
}

export default AlternativeCard
